import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    CardContent,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export const PatrollersReport = ({
    processedShifts,
    selectedEvents,
    saturdayShiftIds,
    shiftMap,
    patrollerMap,
    eventsMap,
    patrollersPage,
    patrollersRowsPerPage,
    showUnderstaffed,
    showOverstaffed,
    showMissingSaturday,
    onPageChange,
    onRowsPerPageChange,
    getPatrollerNameFormatted,
    getRoleName,
    formatDateTime
}) => {
    // Ensure processedShifts is an array
    const safeProcessedShifts = processedShifts || [];

    // Phase 4: Algorithm Optimization - Use Maps for efficient lookups
    const patrollerReportMap = React.useMemo(() => {
        const map = new Map();

        safeProcessedShifts.forEach(shift => {
            shift.patrollers?.forEach(p => {
                const patrollerInfo = patrollerMap.get(p.id);

                if (!map.has(p.id)) {
                    map.set(p.id, {
                        name: getPatrollerNameFormatted(p.id),
                        lastName: patrollerInfo?.lastName || '',
                        firstName: patrollerInfo?.firstName || '',
                        shifts: []
                    });
                }

                const patrollerData = map.get(p.id);
                patrollerData.shifts.push({
                    shiftId: shift.id,
                    shiftName: shift.name,
                    date: shift.date,
                    start: shift.start,
                    role: getRoleName(p.role, shift.roles)
                });
            });
        });

        return map;
    }, [ safeProcessedShifts, patrollerMap, getPatrollerNameFormatted, getRoleName ]);

    const sortedPatrollers = React.useMemo(() => {
        return Array.from(patrollerReportMap.values())
            .sort((a, b) => {
                // Sort by last name, then first name
                if (a.lastName !== b.lastName) {
                    return a.lastName.localeCompare(b.lastName);
                }

                return a.firstName.localeCompare(b.firstName);
            });
    }, [ patrollerReportMap ]);

    // Get current event for limit comparison - O(1) lookup
    // Combined across ticked events: the required-shift limits are summed.
    const eventLimit = selectedEvents.reduce((total, ev) => total + (eventsMap.get(ev.id)?.limit || 0), 0);

    // Filter patrollers based on switches
    const filteredPatrollers = React.useMemo(() => {
        if (!showUnderstaffed && !showOverstaffed && !showMissingSaturday) {
            return sortedPatrollers;
        }

        return sortedPatrollers.filter(patroller => {
            const shiftCount = patroller.shifts.length;
            const isUnderstaffed = shiftCount < eventLimit;
            const isOverstaffed = shiftCount > eventLimit;
            const hasSaturdayShift = patroller.shifts.some(s => saturdayShiftIds.has(s.shiftId));
            const isMissingSaturday = !hasSaturdayShift;

            // Apply filters - show patroller if ANY of the active filters match
            const matches = [];
            if (showUnderstaffed) matches.push(isUnderstaffed);
            if (showOverstaffed) matches.push(isOverstaffed);
            if (showMissingSaturday) matches.push(isMissingSaturday);

            return matches.some(match => match);
        });
    }, [ sortedPatrollers, showUnderstaffed, showOverstaffed, showMissingSaturday, eventLimit, saturdayShiftIds ]);

    // Paginate patrollers
    const paginatedPatrollers = React.useMemo(() =>
        filteredPatrollers.slice(
            patrollersPage * patrollersRowsPerPage,
            patrollersPage * patrollersRowsPerPage + patrollersRowsPerPage
        ),
        [ filteredPatrollers, patrollersPage, patrollersRowsPerPage ]
    );

    // Calculate summary statistics (use filtered patrollers for consistency)
    const patrollerCount = filteredPatrollers.length;
    const understaffedPatrollers = filteredPatrollers.filter(p => p.shifts.length < eventLimit).length;
    const overstaffedPatrollers = filteredPatrollers.filter(p => p.shifts.length > eventLimit).length;
    const missingSaturdayPatrollers = filteredPatrollers.filter(p => !p.shifts.some(s => saturdayShiftIds.has(s.shiftId))).length;

    return (
        <Box>
            {/* Summary Cards */ }
            <Box sx={ { display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' } }>
                <Card sx={ { minWidth: 120 } }>
                    <CardContent>
                        <Typography variant="h6" color="primary">{ patrollerCount }</Typography>
                        <Typography variant="body2" color="text.secondary">Patroller Count</Typography>
                    </CardContent>
                </Card>

                <Card sx={ { minWidth: 120 } }>
                    <CardContent>
                        <Typography variant="h6" color="warning.main">{ understaffedPatrollers }</Typography>
                        <Typography variant="body2" color="text.secondary">Understaffed</Typography>
                    </CardContent>
                </Card>

                <Card sx={ { minWidth: 120 } }>
                    <CardContent>
                        <Typography variant="h6" color="error.main">{ overstaffedPatrollers }</Typography>
                        <Typography variant="body2" color="text.secondary">Overstaffed</Typography>
                    </CardContent>
                </Card>

                <Card sx={ { minWidth: 120 } }>
                    <CardContent>
                        <Typography variant="h6" color="info.main">{ missingSaturdayPatrollers }</Typography>
                        <Typography variant="body2" color="text.secondary">Missing Saturday</Typography>
                    </CardContent>
                </Card>
            </Box>

            { paginatedPatrollers.map((patroller) => {
                // Calculate color based on shift count vs event limit
                let summaryColor = 'text.primary'; // default

                if (patroller.shifts.length < eventLimit) {
                    summaryColor = 'warning.main';
                } else if (patroller.shifts.length === eventLimit) {
                    summaryColor = 'text.primary';
                } else if (patroller.shifts.length > eventLimit) {
                    summaryColor = 'error.main';
                }

                // Check if patroller has any Saturday shifts - O(1) lookup using pre-computed set
                const hasSaturdayShift = patroller.shifts.some(s => saturdayShiftIds.has(s.shiftId));

                return (
                    <Accordion key={ patroller.name }>
                        <AccordionSummary expandIcon={ <ExpandMoreIcon /> }>
                            <Typography variant="h6" sx={ { color: summaryColor } }>
                                { patroller.name } ({ patroller.shifts.length } shift{ patroller.shifts.length !== 1 ? 's' : '' })
                                { !hasSaturdayShift && ' - No Saturday Shift' }
                            </Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                            <TableContainer component={ Paper }>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Typography variant="body1">Shift Name</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body1">Role</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body1">Assigned Shift</Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        { patroller.shifts.map((s, index) => {
                                            // Use shiftMap for O(1) lookup instead of array.find
                                            const shift = shiftMap.get(s.shiftId);
                                            const isSaturday = shift ? shift.isSaturday : false;

                                            return (
                                                <TableRow
                                                    key={ index }
                                                    sx={ {
                                                        p: 2,
                                                        color: isSaturday ? 'info.main' : 'text.primary'
                                                    } }
                                                >
                                                    <TableCell>
                                                        <Typography>
                                                            { s.shiftName }
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell>
                                                        <Typography>
                                                            { s.role }
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell>
                                                        <Typography>
                                                            { formatDateTime(s.date, s.start) }
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }) }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                );
            }) }
            <TablePagination
                component="div"
                count={ filteredPatrollers.length }
                page={ patrollersPage }
                onPageChange={ onPageChange }
                rowsPerPage={ patrollersRowsPerPage }
                onRowsPerPageChange={ onRowsPerPageChange }
                rowsPerPageOptions={ [ 5, 10, 25, 50 ] }
            />
        </Box>
    );
};